import React from "react";
import { Box } from "@mui/material";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useRiskAreaMatrixStyles } from "./RiskAreaMatrix.styles";
import { riskCellColor } from "./RiskAreaMatrix.utils";
import { RiskLevelIndicator } from "../riskLevels/riskLevelIndicators";
import { RiskLevelLegend } from "../riskLevels/RiskLevelLegend";

export interface IRiskAreaMatrix {
    scale: number;
    consequenceLevels: string[];
    probabilityLevels: string[];
    riskLevelIndicators: RiskLevelIndicator[];
    riskLevels: string[];
    matrix: (number | null)[][];
    onCellChange?: (consequenceIndex: number, probabilityIndex: number, value: number | null) => void;
    highlightEmptyCells?: boolean;
    readOnly?: boolean;
    // Keeps the editable layout, values and badges but refuses input. Distinct from readOnly, which renders the
    // counts in place of the risk values: a locked matrix still has to show the criteria it is locking.
    disabled?: boolean;
    // Every grid below is indexed [consequence][probability] and must be scale by scale, like the matrix itself:
    // a grid of another vintage (counts asked for at the saved scale, say) has rows the cells ask for and do not
    // find. Where the two can disagree, the caller passes nothing rather than a grid that half fits.
    //
    // In read-only mode, render this per-cell count instead of the risk value (cells with 0 render empty).
    // The cell keeps its risk-level colour; used by the governance overview to show entity clustering.
    // While editing, the same counts are shown as a corner badge alongside the input, so the number of
    // assessments a cell would re-level is visible before the value is touched.
    cellBadges?: (number | null)[][];
    // In read-only mode, the signed change in each cell's count versus the current view: positive renders an up
    // arrow, negative a down arrow, zero nothing. Used by the after-mitigation governance view to show movement.
    cellMovements?: number[][];
    selectedConsequence?: number | null;
    selectedProbability?: number | null;
    onCellClick?: (consequence: number, probability: number) => void;
}

const movementArrowUpIcon = (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.19288 12.3851L12.3853 3.19268" stroke="#F7447A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.3745 9.54596V3.182H6.01056" stroke="#F7447A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const movementArrowDownIcon = (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.3745 3.18201L3.18213 12.3744" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.18213 6.01044V12.3744H9.5461" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export function RiskAreaMatrix(props: IRiskAreaMatrix) {
    const styles = useRiskAreaMatrixStyles();
    const { translateString } = useTranslation();

    // Probability runs down the y-axis (highest at the top); consequence runs along the x-axis (lowest on the left),
    // with its legends and axis label at the bottom.
    const probabilityRowsHighToLow = Array.from({ length: props.scale }, (_, i) => props.scale - 1 - i);

    // Cells hold a single digit, so select it on focus: typing then replaces the value instead of appending to it.
    const selectCellValue = (event: React.FocusEvent<HTMLDivElement>) => {
        if (event.target instanceof HTMLInputElement) event.target.select();
    };

    const isSelected = (consequenceIndex: number, probabilityIndex: number) =>
        props.selectedConsequence === consequenceIndex + 1 && props.selectedProbability === probabilityIndex + 1;

    return (
        <Box
            sx={{
                display: "grid",
                // Leftmost column holds the vertical Probability label; the next column holds the row headers.
                gridTemplateColumns: `auto minmax(72px, auto) repeat(${props.scale}, minmax(56px, 1fr))`,
                gap: 1,
                alignItems: "center",
            }}
        >
            {/* Probability label spans only the data rows, so it centers on the cells (not the legend rows below). */}
            <Box sx={[styles.verticalAxisLabel, { gridColumn: "1", gridRow: `1 / span ${props.scale}` }]}>
                <Box sx={styles.axisLabel}>{translateString("probability")}</Box>
            </Box>

            {probabilityRowsHighToLow.map((probabilityIndex) => (
                <React.Fragment key={probabilityIndex}>
                    <Box sx={styles.matrixRowHeader}>{props.probabilityLevels[probabilityIndex]}</Box>
                    {props.consequenceLevels.map((_, consequenceIndex) => {
                        const value = props.matrix[consequenceIndex][probabilityIndex];
                        if (props.readOnly) {
                            const badge = props.cellBadges?.[consequenceIndex][probabilityIndex];
                            const content = props.cellBadges ? badge || "" : value;
                            const movement = props.cellMovements?.[consequenceIndex][probabilityIndex];
                            return (
                                <Box
                                    key={consequenceIndex}
                                    onClick={props.onCellClick ? () => props.onCellClick!(consequenceIndex + 1, probabilityIndex + 1) : undefined}
                                    sx={[
                                        styles.matrixCell,
                                        styles.readOnlyMatrixCell,
                                        { backgroundColor: riskCellColor(value, props.riskLevelIndicators) },
                                        isSelected(consequenceIndex, probabilityIndex) && styles.selectedMatrixCell,
                                        !!props.onCellClick && styles.clickableMatrixCell,
                                    ]}
                                >
                                    {!!movement && (
                                        <Box sx={styles.cellMovement}>
                                            {movement > 0 ? movementArrowUpIcon : movementArrowDownIcon}
                                            {Math.abs(movement)}
                                        </Box>
                                    )}
                                    {content}
                                </Box>
                            );
                        }

                        const assessmentsOnCell = props.cellBadges?.[consequenceIndex][probabilityIndex];

                        return (
                            <Box
                                key={consequenceIndex}
                                onFocus={selectCellValue}
                                sx={[
                                    styles.matrixCell,
                                    styles.editableMatrixCell,
                                    { backgroundColor: riskCellColor(value, props.riskLevelIndicators) },
                                    !!props.highlightEmptyCells && value === null && styles.matrixCellError,
                                ]}
                            >
                                {!!assessmentsOnCell && (
                                    <Box sx={styles.cellBadge} title={translateString("riskAreaMatrixCellAssessments", { count: assessmentsOnCell })}>
                                        {assessmentsOnCell}
                                    </Box>
                                )}
                                <DotLegalTextField
                                    label=""
                                    debounce={false}
                                    size="small"
                                    disabled={props.disabled}
                                    onlyNumber="integer"
                                    minNumberValue={1}
                                    maxNumberValue={props.scale}
                                    value={value?.toString() ?? ""}
                                    onChange={(newValue) => props.onCellChange?.(consequenceIndex, probabilityIndex, newValue === "" ? null : Number(newValue))}
                                />
                            </Box>
                        );
                    })}
                </React.Fragment>
            ))}

            {/* Legend row: spacers under the axis-label and row-header columns, then the consequence legends. */}
            <Box />
            <Box />
            {props.consequenceLevels.map((label, consequenceIndex) => (
                <Box key={consequenceIndex} sx={styles.matrixColumnHeader}>
                    {label}
                </Box>
            ))}

            {/* Consequence label spans only the data columns, so it centers on the cells. */}
            <Box sx={[styles.axisLabel, styles.horizontalAxisLabel, { gridColumn: "3 / -1" }]}>{translateString("consequence")}</Box>

            {/* Risk level legend, spanning the full grid width. */}
            <Box sx={{ gridColumn: "3 / -1" }}>
                <RiskLevelLegend riskLevels={props.riskLevels} riskLevelIndicators={props.riskLevelIndicators} />
            </Box>
        </Box>
    );
}
